import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { KpiIds } from '@core/models/kpi.model';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { ICONS } from '@core/services/icons/icons.service';
import { UserPreferencesActions } from '@core/store/user-preferences/user-preferences.actions';
import { selectPlanPanelKpiHidden } from '@core/store/user-preferences/user-preferences.selectors';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { matchesSearch } from '@shared/utils/matches-search';

interface PickerOption {
  id: KpiIds;
  label: string;
  shown: boolean;
  toggle: () => void;
}

/** Which KPIs the panel shows; the plan overview's cards keep a list of their own. */
@Component({
  selector: 'app-kpi-picker',
  imports: [CheckboxComponent, DropdownComponent, TranslatePipe],
  templateUrl: './kpi-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: ``
})
export class KpiPickerComponent {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly lang = inject(CurrentLangService).currentLang;
  protected readonly ICONS = ICONS;

  /** The plan's KPI ids, in display order. */
  readonly available = input.required<KpiIds[]>();

  private readonly hidden = this.store.selectSignal(selectPlanPanelKpiHidden);
  protected readonly isOpen = signal(false);
  protected readonly query = signal('');

  protected readonly options = computed<PickerOption[]>(() => {
    this.lang();
    const hidden = new Set(this.hidden());
    const query = this.query();
    return this.available()
      .map((id) => ({
        id,
        label: this.translate.instant(`kpis.${id}`),
        shown: !hidden.has(id),
        toggle: () =>
          this.store.dispatch(
            UserPreferencesActions.togglePlanPanelKpiVisibility({ kpiId: id })
          )
      }))
      .filter((option) => matchesSearch(option.label, query));
  });

  protected toggleOpen(): void {
    this.isOpen.update((open) => !open);
  }

  protected readonly onClose = (): void => {
    this.isOpen.set(false);
    this.query.set('');
  };

  protected clearSearch(input: HTMLInputElement): void {
    this.query.set('');
    input.focus();
  }

  protected onSearch(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }
}
